// src/prototype/artifacts/renderers/ArtifactRenderer.tsx
// ============================================================
// Top-level artifact renderer. Accepts a DvaArtifact and
// renders its header, summary, metadata badge, and all sections.
// ============================================================

import React from 'react';
import {
  DvaArtifact,
  ARTIFACT_DISPLAY_LABELS,
  ARTIFACT_STATUS_LABELS,
  ARTIFACT_STATUS_COLORS,
} from '../types/artifactTypes';
import { ArtifactSectionRenderer } from './ArtifactSectionRenderer';
import { MarkdownProse } from '../../components/MarkdownProse';
import { formatPlainExecutiveText } from '../../utils/executiveCopy';

interface ArtifactRendererProps {
  artifact: DvaArtifact;
}

export const ArtifactRenderer: React.FC<ArtifactRendererProps> = ({ artifact }) => {
  const statusLabel  = ARTIFACT_STATUS_LABELS[artifact.metadata.status];
  const statusColor  = ARTIFACT_STATUS_COLORS[artifact.metadata.status];
  const typeLabel    = ARTIFACT_DISPLAY_LABELS[artifact.artifactType];
  const generatedDate = new Date(artifact.metadata.generatedAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  const title = formatPlainExecutiveText(artifact.title);
  const subtitle = artifact.subtitle ? formatPlainExecutiveText(artifact.subtitle) : null;

  return (
    <div className="space-y-4">

      {/* Artifact Header */}
      <div data-export-block className="bg-[#18365E] rounded-xl px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {/* Type label */}
            <p className="text-[10px] font-semibold text-white/60 uppercase tracking-widest mb-1">
              {typeLabel}
            </p>
            {/* Title */}
            <h2 className="text-base font-bold text-white leading-tight">{title}</h2>
            {/* Subtitle */}
            {subtitle && (
              <p className="text-sm text-white/80 mt-0.5">{subtitle}</p>
            )}
          </div>
          {/* Status badge */}
          <span className={`shrink-0 text-[10px] font-semibold px-2.5 py-1 rounded-full ${statusColor}`}>
            {statusLabel}
          </span>
        </div>

        {/* Summary */}
        {artifact.summary && (
          <div className="mt-3 leading-relaxed border-t border-white/10 pt-3">
            <MarkdownProse tone="inverse">{artifact.summary}</MarkdownProse>
          </div>
        )}

        {/* Metadata row */}
        <div className="flex items-center gap-3 mt-3 border-t border-white/10 pt-3">
          <span className="text-[10px] text-white/50">v{artifact.metadata.version}</span>
          <span className="text-white/20">·</span>
          <span className="text-[10px] text-white/50">Generated {generatedDate}</span>
          {artifact.metadata.confidenceScore && (
            <>
              <span className="text-white/20">·</span>
              <span className="text-[10px] text-white/50">{artifact.metadata.confidenceScore}% confidence</span>
            </>
          )}
        </div>
      </div>

      {/* Sections */}
      {artifact.sections.map(section => (
        <ArtifactSectionRenderer key={section.sectionId} section={section} />
      ))}

    </div>
  );
};